import { ReactNode } from "react";
import Flex from "../components/Flex";
import Text from "../components/Text";
import { formatDateTime } from "../utils/date-time-utility";
import { DEFAULT_FONTSIZE } from "../utils/utils";

const borderStyle: any = {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
  padding: "2px",
};

export const sectionHeadStyle: any = {
  fontWeight: "bold",
  margin: "0",
  padding: "2px",
};

export const preLineStyle: any = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  pageBreakInside: "avoid",
  breakInside: "avoid",
  minHeight: "1em",
};

export const SectionHead = ({
  label,
  color,
  fontSize,
  borderTop = true,
}: {
  label: ReactNode;
  color?: string;
  fontSize?: string;
  borderTop?: boolean;
}) => {
  return (
    <p
      style={{
        ...sectionHeadStyle,
        backgroundColor: color ?? "#FAF0E6",
        fontSize: fontSize ?? "16px",
        borderTop: borderTop ? "1px solid #000" : "none",
        pageBreakAfter: "avoid",
        breakAfter: "avoid",
        // Keep the band atomic so html2pdf can never split it across pages and
        // strand the top border alone at the bottom of a page.
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}
    >
      {label}
    </p>
  );
};

interface ListContentProps {
  list: any[];
  renderItem?: (item: any, ind: number) => ReactNode;
}

export const ListContent = ({ list, renderItem }: ListContentProps) => {
  return (
    <div style={{ padding: "2px" }}>
      <ul style={{ margin: "0", padding: "4px" }}>
        {list?.map((item, ind) =>
          renderItem ? renderItem(item, ind) : <li>{item}</li>
        )}
      </ul>
    </div>
  );
};

// Class hooks the encounter PDF stylesheet (ENCOUNTER_PDF_PRINT_STYLES) uses to
// decide what may be split across pages. Kept here so the markup and the
// pagination rules that target it stay in one place.
export const REPORT_SECTION_CLASS = "report-section";
export const REPORT_CARD_CLASS = "report-card";
/** Cards whose height grows with the data — they page-break instead of jumping. */
export const REPORT_CARD_FLOW_CLASS = "report-card--flow";

interface ContainerProps {
  children: ReactNode;
  label: ReactNode;
  color?: string;
  fontSize?: string;
  borderTop?: boolean;
  /**
   * Set on cards that list records (prescriptions, codes, orders). Their height
   * follows the data, so they are allowed to continue on the next page rather
   * than being pushed to it whole and leaving the rest of the page blank.
   */
  flow?: boolean;
}

export const ReportContainer = ({
  children,
  label,
  color,
  fontSize,
  borderTop = true,
  flow = false,
}: ContainerProps) => {
  return (
    <div
      className={
        flow
          ? `${REPORT_CARD_CLASS} ${REPORT_CARD_FLOW_CLASS}`
          : REPORT_CARD_CLASS
      }
      style={{
        ...borderStyle,
        width: "100%",
        fontSize: fontSize ?? "16px",
      }}
    >
      <SectionHead
        label={label}
        color={color}
        fontSize={fontSize}
        borderTop={borderTop}
      />
      {children}
    </div>
  );
};

// A top-level section of the encounter-summary report: a labeled band with its
// sub-fields indented underneath, mirroring how the visit-note editor groups
// fields under Subjective / Objective / Assessment / Plan. Renders the header
// unconditionally so the reader always sees the note's structure; the child
// blocks self-hide (return null) when they have no data. The band color is
// deliberately distinct from the linen sub-section (ReportContainer) headers so
// the hierarchy reads at a glance.
export const ReportSection = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => (
  <div className={REPORT_SECTION_CLASS} style={{ width: "100%" }}>
    <SectionHead label={label} color="#D6E4F0" fontSize="18px" />
    <div style={{ paddingLeft: "16px" }}>{children}</div>
  </div>
);

export const KeyValPair = ({ k, v }: { k: string; v: string }) => {
  return (
    <p style={{ margin: "5px 0", fontSize: DEFAULT_FONTSIZE.fontSize }}>
      <strong>{k} : </strong> {v}
    </p>
  );
};

// ICD / CPT code rows. Printed as flat `code : description` lines rather than
// the bulleted <ul> ListContent produces, so the code column lines up under the
// sub-section header the same way the Psychiatric Progress Exam report prints
// its "ICD Code" / "CPT Code" blocks. Shared so every note template renders
// codes identically.
export const CodeList = ({
  codes,
}: {
  codes: { code?: string; description?: string }[];
}) => (
  <div style={{ padding: "0 6px" }}>
    {codes?.map((item, ind) => (
      <p
        key={ind}
        style={{ margin: "1px 0", fontSize: DEFAULT_FONTSIZE.fontSize }}
      >
        <strong>{item?.code} : </strong> {item?.description}
      </p>
    ))}
  </div>
);

export const checkNull = <T,>(value: Array<T> | string) => {
  if (!value || value?.length <= 0) return false;

  return true;
};

type RenderTextWithContainerPatternProps = {
  label: string;
  val?: string | null;
};

export const RenderTextWithContainerPattern = ({
  label,
  val,
}: RenderTextWithContainerPatternProps) => {
  if (!val?.trim()) return null;
  // Free text has no upper bound, so the block continues across a page break
  // rather than jumping to the next page whole. Individual lines stay atomic
  // through `preLineStyle`.
  return (
    <ReportContainer label={label} flow>
      <div style={{ paddingLeft: "5px", fontSize: DEFAULT_FONTSIZE.fontSize }}>
        {val.split("\n").map((line, i) => (
          <div key={i} style={preLineStyle}>
            {line || " "}
          </div>
        ))}
      </div>
    </ReportContainer>
  );
};

export const OrderList = ({ orders }: { orders: any[] }) => {
  if (!checkNull(orders)) return null;

  return (
    <ReportContainer label="Orders" flow>
      <ListContent
        list={orders}
        renderItem={(item, ind) => (
          <Flex flexDirection={"column"} gap={1}>
            <Flex gap={0.5}>
              <Text fontWeight={"bold"}>{ind + 1}.</Text>
              <Text>{`${item?.orderTitle || ""}, ${
                item?.orderType ?? ""
              } - ${formatDateTime(item?.collectionDateTime)} - ${
                item?.userName ?? ""
              } `}</Text>
            </Flex>
            <Flex flexDirection={"column"} gap={0.5}>
              <Text fontWeight={"bold"}>Tests:</Text>
              {item?.tests
                ? Object.values(item?.tests ?? {}).map(
                    (it: any, ind: number) => {
                      return (
                        <Flex gap={0.5} key={it.uuid}>
                          <Text>{ind + 1}.</Text>
                          <Text>{it}</Text>
                        </Flex>
                      );
                    }
                  )
                : ""}
            </Flex>
          </Flex>
        )}
      />
    </ReportContainer>
  );
};

// Renders the `therapyNotes` array ([{ name, description }]) — the therapies
// recommended during the visit — as a numbered list.
export const TherapyNotesList = ({ therapyNotes }: { therapyNotes: any[] }) => {
  if (!checkNull(therapyNotes)) return null;

  // Sort by name so the summary matches the visit-note therapy order (ECRH-5428).
  const sortedTherapyNotes = [...therapyNotes].sort((a, b) =>
    (a?.name ?? "").localeCompare(b?.name ?? "")
  );

  return (
    <ReportContainer label="Therapies" flow>
      <ListContent
        list={sortedTherapyNotes}
        renderItem={(item, ind) => (
          <Flex flexDirection={"column"} gap={0.5} key={item?.uuid ?? ind}>
            <Flex gap={0.5}>
              <Text fontWeight={"bold"}>{ind + 1}.</Text>
              <Text fontWeight={"bold"}>{item?.name ?? ""}</Text>
            </Flex>
            {item?.description && <Text>{item?.description}</Text>}
          </Flex>
        )}
      />
    </ReportContainer>
  );
};

export const CarePlanTemplateList = ({
  carePlanTemplates,
}: {
  carePlanTemplates: any[];
}) => {
  if (!checkNull(carePlanTemplates)) return null;

  return (
    <ReportContainer label="Care Plan Templates" flow>
      <ListContent
        list={carePlanTemplates}
        renderItem={(item, ind) => (
          <Flex flexDirection={"column"} gap={1}>
            <Flex gap={0.5}>
              <Text fontWeight={"bold"}>{ind + 1}.</Text>
              <Text>{`${item?.templateName || ""}`}</Text>
            </Flex>
            <Flex flexDirection={"column"} gap={0.5}>
              <Text fontWeight={"bold"}>Goals :</Text>
              <Text>{item?.goals ?? ""}</Text>
              <Text fontWeight={"bold"}>Intervention & Tasks :</Text>
              <Text>{item?.intervention ?? ""}</Text>
              <Text fontWeight={"bold"}>Preventive Schedule :</Text>
              <Text>{item?.preventive_schedule ?? ""}</Text>
            </Flex>
          </Flex>
        )}
      />
    </ReportContainer>
  );
};

/* ── Psychiatric Progress Exam report ──
   SOAP bands and sub-section headers for PsychiatricProgressTemplate, in the
   report's SOAP order. Kept alongside the shared report
   primitives so the note's headers and the blocks that render them live
   together. */

/** SOAP bands, in print order. */
export const PSYCH_PROGRESS_SOAP_SECTION = {
  SUBJECTIVE: "Subjective",
  OBJECTIVE: "Objective",
  ASSESSMENT: "Assessment",
  PLAN: "Plan",
} as const;

/** Sub-section headers rendered inside the SOAP bands. */
export const PSYCH_PROGRESS_REPORT_LABEL = {
  SUBJECTIVE_NOTE: "Subjective Note",
  HISTORY_OF_PRESENT_ILLNESS: "History of Present Illness",
  PATIENT_REPORTED_HISTORY: "Patient-Reported History",
  MENTAL_STATUS_EXAM: "Mental Status Examination (MSE)",
  INSIGHT_JUDGMENT: "Insight & Judgment",
  ICD_CODE: "ICD Code",
  CPT_CODE: "CPT Code",
  ASSESSMENT: "Assessment",
  THERAPY: "Therapy",
  ENTER_NOTE: "Enter Note",
  FOLLOW_UP: "Follow Up",
} as const;

/**
 * Mental Status Exam fields — rendered in the exact top-to-bottom order they
 * appear in the encounter form (PsychiatricProgressExam.tsx → PSYCH_PROGRESS_EXAM_FIELDS).
 * Labels are spelled out explicitly so camelCase keys (e.g. `thoughtProcess`)
 * render with proper spacing.
 */
export const MENTAL_STATUS_EXAM_FIELDS: { key: string; label: string }[] = [
  { key: "appearance", label: "Appearance" },
  { key: "behavior", label: "Behavior" },
  { key: "speech", label: "Speech" },
  { key: "mood", label: "Mood" },
  { key: "affect", label: "Affect" },
  { key: "thoughtProcess", label: "Thought Process & Content" },
  { key: "perceptions", label: "Perceptions" },
  { key: "cognition", label: "Cognition" },
];

/**
 * Plan section fields — order mirrors the encounter form
 * (PSYCH_PROGRESS_EXAM_PLAN_FIELDS). `treatment` is surfaced as "Plan".
 */
export const PLAN_FIELDS: { key: string; label: string }[] = [
  { key: "treatment", label: "Plan" },
  { key: "futureConsiderations", label: "Future Considerations" },
  { key: "labStudy", label: "Lab Study" },
  { key: "referrals", label: "Referrals" },
  { key: "collateral", label: "Collateral" },
  { key: "safety", label: "Safety" },
];
